import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * PhoneNumberManager — manage Twilio phone numbers for SMS/calling.
 * Embeddable in UserProfile or standalone.
 */
const PhoneNumberManager = () => {
  const [myNumbers, setMyNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsStatus, setSmsStatus] = useState(null);
  
  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [areaCode, setAreaCode] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [releasing, setReleasing] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadData = async () => {
    try {
      const [statusRes, numbersRes] = await Promise.all([
        fetch(`${API}/api/outreach/sms-status`, { headers }),
        fetch(`${API}/api/outreach/phone-numbers/my`, { headers })
      ]);
      if (statusRes.ok) setSmsStatus(await statusRes.json());
      if (numbersRes.ok) setMyNumbers(await numbersRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const searchNumbers = async () => {
    if (!areaCode || areaCode.length < 3) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await fetch(`${API}/api/outreach/phone-numbers/search`, {
        method: 'POST', headers,
        body: JSON.stringify({ area_code: areaCode })
      });
      if (res.ok) setSearchResults(await res.json());
      else {
        const err = await res.json();
        alert(err.error || 'Search failed');
      }
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  const purchaseNumber = async (phoneNumber) => {
    if (!window.confirm(`Purchase ${phoneNumber}? This will charge ~$1.15/month to your Twilio account.`)) return;
    setPurchasing(phoneNumber);
    try {
      const res = await fetch(`${API}/api/outreach/phone-numbers/purchase`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone_number: phoneNumber })
      });
      if (res.ok) {
        setShowSearch(false);
        setSearchResults([]);
        loadData();
      } else {
        const err = await res.json();
        alert(err.error || 'Purchase failed');
      }
    } catch (e) { console.error(e); alert('Purchase failed'); }
    finally { setPurchasing(null); }
  };

  const releaseNumber = async (id) => {
    if (!window.confirm('Release this phone number? It will be removed from your account and returned to Twilio.')) return;
    setReleasing(id);
    try {
      const res = await fetch(`${API}/api/outreach/phone-numbers/${id}`, { method: 'DELETE', headers });
      if (res.ok) loadData();
      else {
        const err = await res.json();
        alert(err.error || 'Release failed');
      }
    } catch (e) { console.error(e); }
    finally { setReleasing(null); }
  };

  const formatPhone = (p) => {
    if (!p) return '';
    const cleaned = p.replace(/[^+\d]/g, '');
    if (cleaned.length === 12 && cleaned.startsWith('+1')) {
      return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
    }
    return p;
  };

  if (loading) return <div className="py-4 text-center text-sm text-gray-400">Loading phone settings...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Phone & SMS</h3>
          <p className="text-xs text-gray-500 mt-0.5">Your Twilio phone number for outreach SMS and calling</p>
        </div>
        {smsStatus?.configured ? (
          <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Twilio Connected</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-600 rounded-full font-medium">Twilio Not Configured</span>
        )}
      </div>

      {!smsStatus?.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          Twilio is not configured. An administrator needs to set <code className="bg-amber-100 px-1 rounded">TWILIO_ACCOUNT_SID</code> and <code className="bg-amber-100 px-1 rounded">TWILIO_AUTH_TOKEN</code> in the server environment.
        </div>
      )}

      {/* Current numbers */}
      {myNumbers.length > 0 ? (
        <div className="space-y-2">
          {myNumbers.map(num => (
            <div key={num.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{formatPhone(num.phone_number)}</div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-2 mt-0.5">
                    {num.capabilities?.sms && <span className="bg-teal-50 text-teal-600 px-1.5 py-0.5 rounded">SMS</span>}
                    {num.capabilities?.voice && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Voice</span>}
                    <span>Added {new Date(num.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => releaseNumber(num.id)} disabled={releasing === num.id}
                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
                {releasing === num.id ? 'Releasing...' : 'Release'}
              </button>
            </div>
          ))}
        </div>
      ) : smsStatus?.configured ? (
        <div className="bg-gray-50 border border-dashed rounded-lg p-6 text-center">
          <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
          <p className="text-sm text-gray-500">No phone number assigned</p>
          <p className="text-xs text-gray-400 mt-1">Get a phone number to send SMS and make calls through the platform</p>
          <button onClick={() => setShowSearch(true)}
            className="mt-3 px-4 py-2 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium">
            Get a Phone Number
          </button>
        </div>
      ) : null}

      {/* Get number button (when user has numbers but wants to add more — currently limited to 1) */}
      {myNumbers.length === 0 && smsStatus?.configured && !showSearch && myNumbers.length > 0 && null}

      {/* Search panel */}
      {showSearch && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">Search Available Numbers</h4>
            <button onClick={() => { setShowSearch(false); setSearchResults([]); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
          </div>
          <div className="flex items-center gap-2">
            <input type="text" value={areaCode} onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="Area code (e.g. 212)"
              className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500"
              onKeyDown={(e) => e.key === 'Enter' && searchNumbers()} />
            <button onClick={searchNumbers} disabled={searching || areaCode.length < 3}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="max-h-64 overflow-auto space-y-1">
              {searchResults.map(n => (
                <div key={n.phone_number} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                  <div>
                    <span className="text-sm font-mono font-medium text-gray-900">{formatPhone(n.phone_number)}</span>
                    <span className="text-xs text-gray-400 ml-2">{n.locality}{n.region ? `, ${n.region}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">~${n.monthly_cost}/mo</span>
                    <button onClick={() => purchaseNumber(n.phone_number)}
                      disabled={purchasing === n.phone_number}
                      className="px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50">
                      {purchasing === n.phone_number ? 'Purchasing...' : 'Get This Number'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searching && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            </div>
          )}

          {!searching && searchResults.length === 0 && areaCode.length >= 3 && (
            <p className="text-xs text-gray-400 text-center py-2">No results found. Try a different area code.</p>
          )}
        </div>
      )}

      {/* Get number button when user has no numbers */}
      {myNumbers.length === 0 && smsStatus?.configured && !showSearch && (
        <button onClick={() => setShowSearch(true)}
          className="w-full py-2 text-xs border border-dashed border-teal-300 rounded-lg text-teal-600 hover:bg-teal-50">
          + Get a Phone Number
        </button>
      )}
    </div>
  );
};

export default PhoneNumberManager;
